import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Upload, Download, X, Search, Eye, ChevronDown } from 'lucide-react';
import AgGrid from '../../components/common/AgGrid';
import Icon from '../common/Icon';
import {
    GetJobDetailsbyUserId,
    GetTemplateOfLoL,
    UploadListOfList,
    GetListOfListDetails,
    GetJobTypes,
    GetBatchDetailsListOfList,
    GetListOfListsVersions
} from '../../services/accountService';
import SuccessMessage from '../common/SuccessMessage';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import { isListAgent } from '../../utils/PermissionUtils';

import { useNavigate, useLocation } from 'react-router-dom';


const STATUS_OPTIONS = [
    { value: 'SUCCESS', label: 'Successful' },
    { value: 'FAILED', label: 'Errors Found' },
    { value: 'PENDING', label: 'Pending' }
];


const INITIAL_APPLIED_FILTERS = {
    clientIds: [],
    jobId: '',
    jobTypeId: '',
    mailDateFrom: '',
    mailDateTo: '',
    status: ''
};


const TooltipText = ({ text, maxLength = 25, customDisplay = null }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const textRef = useRef(null);

    const needsTruncation = customDisplay
        ? customDisplay !== text
        : (text && text.length > maxLength);

    const displayText = customDisplay
        ? customDisplay
        : (needsTruncation ? `${text.substring(0, maxLength)}...` : text);

    const handleMouseEnter = (e) => {
        if (needsTruncation) {
            const rect = e.target.getBoundingClientRect();
            setTooltipPosition({
                top: rect.top - 35,
                left: rect.left + (rect.width / 2)
            });
            setShowTooltip(true);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    return (
        <div
            ref={textRef}
            style={{ position: 'relative', display: 'inline-block', width: '100%' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <span style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: needsTruncation ? 'help' : 'default'
            }}>
                {displayText}
            </span>
            {showTooltip && needsTruncation && (
                <div
                    style={{
                        position: 'fixed',
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: 'translateX(-50%)',
                        backgroundColor: '#333',
                        color: '#fff',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        maxWidth: '300px',
                        wordWrap: 'break-word',
                        zIndex: 10000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        whiteSpace: 'normal',
                        pointerEvents: 'none'
                    }}
                >
                    {text}
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '-6px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: '6px solid #333'
                        }}
                    />
                </div>
            )}
        </div>
    );
};

const ListOfList = () => {
    const fileInputRef = useRef(null);
    const clientDropdownRef = useRef(null);
    const uploadVersionDropdownRef = useRef(null);
    const viewOnly = isListAgent(); // ✅ NEW

  
    const [uploadedFile, setUploadedFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isFormatInvalid, setIsFormatInvalid] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    
    const [showFilters, setShowFilters] = useState(false);

    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedClients, setSelectedClients] = useState([]);
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [clientError, setClientError] = useState('');

    const navigate = useNavigate()
    const location = useLocation(); 

  
    const [jobTypes, setJobTypes] = useState([]);
    const [loadingJobTypes, setLoadingJobTypes] = useState(false);
    const [selectedJobTypeId, setSelectedJobTypeId] = useState('');

   
    const [selectedStatus, setSelectedStatus] = useState('');

    
    const [mailDateFrom, setMailDateFrom] = useState('');
    const [mailDateTo, setMailDateTo] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    
    const [jobCode, setJobCode] = useState('');

   
    const [appliedFilters, setAppliedFilters] = useState(INITIAL_APPLIED_FILTERS);

    
    const [searchLoading, setSearchLoading] = useState(false);

   
    const [listUploadData, setListUploadData] = useState([]);
    const [listLoading, setListLoading] = useState(false);

   
    const [batchDetailsRows, setBatchDetailsRows] = useState([]);
    const [batchLoading, setBatchLoading] = useState(false);

    
    const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);

    
    const [uploadVersions, setUploadVersions] = useState([]);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [selectedUploadVersion, setSelectedUploadVersion] = useState(null);
    const [showUploadVersionDropdown, setShowUploadVersionDropdown] = useState(false);

   
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success');

    const isUploadEnabled = !!uploadedFile && !fileError && !isFormatInvalid;

    
    const isTotalRow = (params) =>
        params?.node?.rowPinned === 'bottom' || params?.data?.isTotalsRow;

   
    useEffect(() => {
        const fetchClients = async () => {
            setLoading(true);
            try {
                const clientData = await GetJobDetailsbyUserId();
                setClients(clientData);
            } catch (error) {
                console.error("Error fetching jobs:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchClients();
    }, []);

   
    useEffect(() => {
        fetchListUploadHistory();
    }, []);

   
    useEffect(() => {
        const fetchJobTypes = async () => {
            setLoadingJobTypes(true);
            try {
                const response = await GetJobTypes();
                if (response && response.isSuccess && Array.isArray(response.data)) {
                    setJobTypes(response.data);
                } else {
                    console.error("Invalid job types response format:", response);
                    setJobTypes([]);
                }
            } catch (error) {
                console.error("Error fetching job types:", error);
                setJobTypes([]);
            } finally {
                setLoadingJobTypes(false);
            }
        };
        fetchJobTypes();
    }, []);

  
    const fetchListUploadHistory = async () => {
        setListLoading(true);
        try {
            const response = await GetListOfListDetails();

            if (response?.success) {
                const mappedRows = response.data.map(item => ({
                    id: item.latestUploadID,
                    clientName: item.clientName,
                    clientId: item.clientCode,
                    jobId: item.jobCode,
                    type: item.jobTypeName,
                    status:
                        item.status === 'SUCCESS'
                            ? 'Successful'
                            : item.status === 'FAILED'
                                ? 'Errors Found'
                                : item.status,
                    description: item.description || '',
                    mailDate: item.earliestMailDate
                        ? new Date(item.earliestMailDate).toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric'
                        })
                        : '',
                    _raw: item
                }));

                setListUploadData(mappedRows);
            } else {
                setListUploadData([]);
            }
        } catch (e) {
            console.error('Upload list fetch error:', e);
            setListUploadData([]);
        } finally {
            setListLoading(false);
        }
    };

 
    const fetchUploadVersions = async (jobId) => {
        console.log("Fetching upload versions for jobId:", jobId);
        if (!jobId) {
            return;
        }
        setLoadingVersions(true);
        setUploadVersions([]);
        setSelectedUploadVersion(null);

        try {
            const response = await GetListOfListsVersions(jobId);
            console.log("Versions response:", response);

            if (response?.success && Array.isArray(response.data) && response.data.length > 0) {
                const mappedVersions = response.data.map((item) => ({
                    id: item.listsUploadID,
                    versionNumber: item.versionNumber,
                    name: item.versionLabel,
                    uploadedBy: item.uploadedByUserName,
                    uploadedByUserID: item.uploadedByUserID,
                    uploadedDateTime: item.uploadedDateTime,
                    sourceSystem: item.sourceSystem,
                    versionStatus: item.versionStatus,
                    isLatestVersion: item.isLatestVersion,
                    totalRows: item.totalRows,
                    successCount: item.successCount,
                    errorCount: item.errorCount,
                    _raw: item
                }));

                setUploadVersions(mappedVersions);

                const latestVersion = mappedVersions.find(v => v.isLatestVersion) || mappedVersions[0];
                setSelectedUploadVersion(latestVersion);
            } else {
                setUploadVersions([]);
                setSelectedUploadVersion(null);
            }
        } catch (error) {
            setUploadVersions([]);
            setSelectedUploadVersion(null);
        } finally {
            setLoadingVersions(false);
        }
    };

   
    const filteredClients = useMemo(() => {
        if (!clientSearchTerm) return clients;
        const term = clientSearchTerm.toLowerCase();
        return clients.filter(client =>
            client.clientName?.toLowerCase().includes(term)
        );
    }, [clients, clientSearchTerm]);

   
    const formatFileSize = (bytes) => {
        if (!bytes) return '0 KB';
        return `${(bytes / 1024).toFixed(2)} KB`;
    };

    const validateFile = (file) => {
        if (!file) return 'File is required';
        const allowedTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ];
        if (!allowedTypes.includes(file.type)) {
            return 'Only Excel (.xlsx or .xls) files are allowed';
        }
        return '';
    };

    const extractHeadersFromExcel = (source) => {
        return new Promise(async (resolve, reject) => {
            try {
                const XLSX = await import('xlsx');

                const readAsArrayBuffer = (input) =>
                    new Promise((res, rej) => {
                        const reader = new FileReader();
                        reader.onload = (e) => res(e.target.result);
                        reader.onerror = () => rej('Read error');
                        reader.readAsArrayBuffer(input);
                    });

                let buffer;
                if (source instanceof File || source instanceof Blob) {
                    buffer = await readAsArrayBuffer(source);
                } else {
                    buffer = source;
                }

                const data = new Uint8Array(buffer);
                const wb = XLSX.read(data, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
                const headers = (rows?.[0] || []).map(h => String(h).trim());
                resolve(headers);
            } catch (err) {
                reject(err);
            }
        });
    };

    const validateFileFormat = async (uploadedFile) => {
        try {
            const uploadedHeaders = await extractHeadersFromExcel(uploadedFile);

            if (uploadedHeaders.length === 0) {
                return 'Uploaded file is empty or has no headers';
            }

            const templateBlob = await GetTemplateOfLoL();
            const templateBlobObj = new Blob([templateBlob], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const templateHeaders = await extractHeadersFromExcel(templateBlobObj);

            if (templateHeaders.length === 0) {
                console.warn('Could not read template headers, skipping format validation');
                return '';
            }

            const missingHeaders = templateHeaders.filter(
                h => !uploadedHeaders.includes(h)
            );

            if (missingHeaders.length > 0) {
                return `Invalid format. Please use template for reference`;
            }

            return '';
        } catch (error) {
            console.error('Format validation error:', error);
            return '';
        }
    };

   
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const typeError = validateFile(file);
        if (typeError) {
            setFileError(typeError);
            setIsFormatInvalid(true);
            setUploadedFile(null);
            return;
        }

        setIsValidating(true);
        setFileError('');
        setIsFormatInvalid(false);

        const formatError = await validateFileFormat(file);

        setIsValidating(false);

        if (formatError) {
            setFileError(formatError);
            setIsFormatInvalid(true);
            setUploadedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setIsFormatInvalid(false);
        setFileError('');
        setUploadedFile(file);
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        setFileError('');
        setIsFormatInvalid(false);
        setIsValidating(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

   
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (!file) return;

        const typeError = validateFile(file);
        if (typeError) {
            setFileError(typeError);
            setIsFormatInvalid(true);
            setUploadedFile(null);
            return;
        }

        setIsValidating(true);
        setFileError('');
        setIsFormatInvalid(false);

        const formatError = await validateFileFormat(file);

        setIsValidating(false);

        if (formatError) {
            setFileError(formatError);
            setIsFormatInvalid(true);
            setUploadedFile(null);
            return;
        }

        setIsFormatInvalid(false);
        setFileError('');
        setUploadedFile(file);
    };

   
    const downloadTemplate = async () => {
        try {
            const response = await GetTemplateOfLoL();
            const blob = new Blob([response], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'LOL_Template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Template download failed:', error);
            alert('Template download failed. Please try again.');
        }
    };

   const handleUploadFileToAPI = async () => {
    if (!uploadedFile) return;

    setIsUploading(true);
    try {
        const response = await UploadListOfList(uploadedFile);

        const errorCount = response?.data?.errorCount || 0;
        const hasProcessingErrors =
            errorCount > 0 || response?.data?.batchStatus === 'FAILED';

        if (response?.hasWarning) {
            setToastType('error');
            setToastMessage(response.warningMessage || 'Uploaded with warnings');
        } else if (response?.success && hasProcessingErrors) {
            setToastType('error');
            setToastMessage('File processed successfully but there are some errors found');
        } else {
            setToastType('success');
            setToastMessage('File uploaded successfully');
        }

        handleRemoveFile();
        fetchListUploadHistory();
    } catch (error) {
        setToastType('error');
        setToastMessage(error.response.data.errors);
    } finally {
        setIsUploading(false);
    }
};

   
    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    const calculateBatchTotals = (rows) => {
        let quantityOrdered = 0;
        let totalActCost = 0;

        rows.forEach(row => {
            if (row.isTotalsRow) return;

           
            quantityOrdered += row._quantityOrdered || 0;

            if (row.totalActCost) {
                totalActCost += Number(
                    String(row.totalActCost)
                        .replace('$', '')
                        .replace(',', '')
                ) || 0;
            }
        });

        return { quantityOrdered, totalActCost };
    };

    const batchTotalsRow = useMemo(() => {
        const totals = calculateBatchTotals(batchDetailsRows);

        return {
            id: '__TOTAL__',
            jobId: '',
            issue: '',
            orderNumber: 'TOTAL',
            listName: '',
            segment: '',
            mailDate: '',
            dateNeeded: '',
            quantityOrdered: totals.quantityOrdered.toLocaleString('en-US'),
            orderType: '',
            listManager: '',
            listtypeid: '',
            totalActCost: `$ ${totals.totalActCost.toFixed(2)}`,
            changeType: '',
            processingStatus: '',
            errorMessage: '',
            issueTooltip: '',
            isTotalsRow: true,
        };
    }, [batchDetailsRows]);


    const handleClientCheckboxChange = (client) => {
        const isSelected = selectedClients.some(c => c.clientID === client.clientID);

        if (isSelected) {
            const newClients = selectedClients.filter(c => c.clientID !== client.clientID);
            setSelectedClients(newClients);
            setSelectedClientIds(newClients.map(c => c.clientID));

            if (newClients.length === 1) {
                setSelectedClient(newClients[0].clientName);
            } else if (newClients.length > 1) {
                setSelectedClient(`${newClients.length} clients selected`);
            } else {
                setSelectedClient('');
            }
        } else {
            const newClients = [...selectedClients, client];
            setSelectedClients(newClients);
            setSelectedClientIds(newClients.map(c => c.clientID));

            if (newClients.length === 1) {
                setSelectedClient(newClients[0].clientName);
            } else if (newClients.length > 1) {
                setSelectedClient(`${newClients.length} clients selected`);
            }

             setClientSearchTerm('');
        }
    };

    const handleSelectAllClients = () => {
        setSelectedClients([...clients]);
        setSelectedClientIds(clients.map(c => c.clientID));
        setSelectedClient(`${clients.length} clients selected`);
    };

    const handleClearAllClients = () => {
        setSelectedClients([]);
        setSelectedClientIds([]);
        setSelectedClient('');
        setClientSearchTerm('');
    };

    const hasSearchCriteria = () => {
        return (
            selectedClientIds.length > 0 ||
            jobCode ||
            selectedJobTypeId ||
            mailDateFrom ||
            mailDateTo ||
            selectedStatus
        );
    };

    useEffect(() => {
        const handleGlobalKeyPress = (e) => {
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                e.preventDefault();


                if (!hasSearchCriteria()) {
                    alert('Please enter at least one search criteria');
                    return;
                }


                handleSearch();
            }
        };

        document.addEventListener('keypress', handleGlobalKeyPress);

        return () => {
            document.removeEventListener('keypress', handleGlobalKeyPress);
        };
    }, [
        selectedClientIds,
        jobCode,
        selectedJobTypeId,
        mailDateFrom,
        mailDateTo,
        selectedStatus
    ]);


    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

   
    const handleSearch = () => {


        
        if (!hasSearchCriteria()) {
            alert('Please enter at least one search criteria');
            return;
        }



        if (mailDateFrom && mailDateTo) {
            if (new Date(mailDateFrom) > new Date(mailDateTo)) {
                alert('Mail Date "From" cannot be after "To"');
                return;
            }
        }

        setSearchLoading(true);

        setAppliedFilters({
            clientIds: selectedClientIds,
            jobId: jobCode?.trim() || '',
            jobTypeId: selectedJobTypeId,
            mailDateFrom,
            mailDateTo,
            status: selectedStatus
        });

        setTimeout(() => setSearchLoading(false), 200);
    };

   
    const handleClear = () => {
        setSelectedClients([]);
        setSelectedClientIds([]);
        setSelectedClient('');
        setClientSearchTerm('');
        setMailDateFrom('');
        setMailDateTo('');
        setSelectedJobTypeId('');
        setSelectedStatus('');
        setJobCode('');
        setAppliedFilters(INITIAL_APPLIED_FILTERS);
    };

  
    const fetchBatchDetails = async (jobId, versionNumber = null) => {
        if (!jobId) {
            return;
        }
        setBatchLoading(true);

        try {
            const response = await GetBatchDetailsListOfList(jobId, versionNumber);

            if (response?.success && response?.data) {
                const { rows = [], versions = [] } = response.data;
                const mappedRows = rows.map((row, index) => ({
                    id: `${row.jobID}-${index}`,
                    jobId: row.jobID,
                    orderNumber: row.orderNumber,
                    listName: row.listName,
                    segment: row.segment,
                    mailDate: row.mailDate
                        ? new Date(row.mailDate).toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric'
                        })
                        : '',
                    dateNeeded: row.dateNeeded
                        ? new Date(row.dateNeeded).toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric'
                        })
                        : '',
                    quantityOrdered: row.quantityOrdered != null
                        ? Number(row.quantityOrdered).toLocaleString('en-US')
                        : '',
                    _quantityOrdered: Number(row.quantityOrdered) || 0,
                    orderType: row.orderType,
                    listManager: row.listManager,
                    listtypeid: row.rentalExchangeFlag,
                    totalActCost: row.totalActCost ? `$${row.totalActCost}` : '$0.00',
                    changeType: row.changeType,
                    processingStatus: row.processingStatus,
                    errorMessage: row.errorMessage || '',
                    issueTooltip: row.processingStatus === 'SUCCESS'
                        ? ''
                        : (row.errorMessage || ''),
                    issue: row.processingStatus === 'SUCCESS' ? 'SUCCESS' : 'Error',
                    versionNumber: row.versionNumber
                }));
                setBatchDetailsRows(mappedRows);

                if (
                    Array.isArray(versions) &&
                    versions.length > 0 &&
                    uploadVersions.length === 0 &&
                    !selectedUploadVersion
                ) {

                    const mappedVersions = versions.map((item) => ({
                        id: item.listsUploadID,
                        versionNumber: item.versionNumber,
                        name: item.versionLabel,
                        uploadedBy: item.uploadedByUserName,
                        versionStatus: item.versionStatus,
                        isLatestVersion: item.isSelected || item.isLatestVersion,
                        errorCount: item.errorCount,
                        successCount: item.successCount,
                        _raw: item
                    }));
                    setUploadVersions(mappedVersions);

                    const selected = mappedVersions.find(v => v._raw.isSelected) || mappedVersions[0];
                    setSelectedUploadVersion(selected);
                }
            } else {
                setBatchDetailsRows([]);
            }
        } catch (error) {
            setBatchDetailsRows([]);
        } finally {
            setBatchLoading(false);
        }
    };

    const handleViewDetails = async (rowData) => {
        setSelectedJobForDetails(rowData);

        const rawJobId = rowData?._raw?.jobID || rowData.jobId;

        await fetchUploadVersions(rawJobId);
        await fetchBatchDetails(rawJobId);
    };

   
    const handleUploadVersionChange = (version) => {
        setSelectedUploadVersion(version);
        setShowUploadVersionDropdown(false);

        const rawJobId = selectedJobForDetails?._raw?.jobID || selectedJobForDetails?.jobId;
        if (rawJobId && version?.versionNumber != null) {
            fetchBatchDetails(rawJobId, version.versionNumber);
        } else {
            console.warn('⚠️ [handleUploadVersionChange] Missing jobId or versionNumber, skipping refetch');
        }
    };

   
    const handleCancelDetails = () => {
        setSelectedJobForDetails(null);
        setBatchDetailsRows([]);
        setUploadVersions([]);
        setSelectedUploadVersion(null);
    };

  
    const filteredData = useMemo(() => {
        const {
            clientIds,
            jobId,
            jobTypeId,
            mailDateFrom: appliedFrom,
            mailDateTo: appliedTo,
            status
        } = appliedFilters;

        const hasAnyFilter =
            (clientIds && clientIds.length > 0) ||
            jobId ||
            jobTypeId ||
            appliedFrom ||
            appliedTo ||
            status;

        if (!hasAnyFilter) return listUploadData;

        return listUploadData.filter(row => {
            const raw = row._raw || {};

            if (clientIds && clientIds.length > 0) {
                if (!clientIds.includes(raw.clientID)) return false;
            }

            if (jobId) {
                const term = jobId.toLowerCase().trim();
                if (!row.jobId?.toLowerCase().includes(term)) return false;
            }

            if (jobTypeId) {
                if (Number(raw.jobTypeID) !== Number(jobTypeId)) return false;
            }

            if (status) {
                if (raw.status !== status) return false;
            }

            if (appliedFrom || appliedTo) {
                if (!raw.earliestMailDate) return false;

                const rowDate = new Date(raw.earliestMailDate);
                rowDate.setHours(0, 0, 0, 0);

                if (appliedFrom) {
                    const fromDate = new Date(appliedFrom);
                    fromDate.setHours(0, 0, 0, 0);
                    if (rowDate < fromDate) return false;
                }

                if (appliedTo) {
                    const toDate = new Date(appliedTo);
                    toDate.setHours(23, 59, 59, 999);
                    if (rowDate > toDate) return false;
                }
            }

            return true;
        });
    }, [listUploadData, appliedFilters]);

    const hasVersionError = useMemo(() => {
        if (!selectedUploadVersion) return false;
        return Boolean(
            selectedUploadVersion.versionStatus === 'FAILED' ||
            (selectedUploadVersion.errorCount && selectedUploadVersion.errorCount > 0)
        );
    }, [selectedUploadVersion]);

   
    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (appliedFilters.clientIds?.length > 0) count++;
        if (appliedFilters.jobId) count++;
        if (appliedFilters.jobTypeId) count++;
        if (appliedFilters.mailDateFrom) count++;
        if (appliedFilters.mailDateTo) count++;
        if (appliedFilters.status) count++;
        return count;
    }, [appliedFilters]);

  
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
                setShowClientDropdown(false);
            }
            if (uploadVersionDropdownRef.current && !uploadVersionDropdownRef.current.contains(event.target)) {
                setShowUploadVersionDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    const statusConfig = {
        Successful: {
            icon: 'active',
            color: '#045a18',
            label: 'Successful'
        },
        'Errors Found': {
            icon: 'in-active',
            color: '#dc3545',
            label: 'Errors Found'
        },
    };

    const DescriptionCellRenderer = (params) => {
        const description = params.value || '';
        const cellRef = useRef(null);
        const [showTooltip, setShowTooltip] = useState(false);
        useEffect(() => {
            const handleMouseEnter = () => { if (description.length > 35) setShowTooltip(true); };
            const handleMouseLeave = () => setShowTooltip(false);
            const cellElement = cellRef.current;
            if (cellElement) {
                cellElement.addEventListener('mouseenter', handleMouseEnter);
                cellElement.addEventListener('mouseleave', handleMouseLeave);
            }
            return () => {
                if (cellElement) {
                    cellElement.removeEventListener('mouseenter', handleMouseEnter);
                    cellElement.removeEventListener('mouseleave', handleMouseLeave);
                }
            };
        }, [description]);
        const truncatedText = description.length > 35 ? description.substring(0, 35) + '...' : description;
        return (
            <div ref={cellRef} className="description-cell"
                style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
                <div style={{
                    whiteSpace: 'normal', wordBreak: 'break-word',
                    cursor: description.length > 35 ? 'pointer' : 'default', flex: 1
                }}>
                    {truncatedText}
                </div>
                {showTooltip && description.length > 35 && (
                    <div className="custom-tooltip">{description}</div>
                )}
            </div>
        );
    };

   
    const columnDefs = useMemo(() => [
        {
            headerName: 'View',
            field: 'view',
            width: 100,
            minWidth: 120,
            sortable: false,
            filter: false,
            cellRenderer: (params) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => handleViewDetails(params.data)}
                >
                    <Eye size={18} color="#2563eb" />
                </div>
            )
        },
        { headerName: 'Client Name', field: 'clientName', flex: 2, minWidth: 300, filter: 'agTextColumnFilter' },
        { headerName: 'Client Code', field: 'clientId', minWidth: 200, filter: 'agTextColumnFilter' },
      

        {
            headerName: 'Job ID',
            field: 'jobId',
            minWidth: 150,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                const jobId = params.value;

                const handleClick = (e) => {
                    e.stopPropagation();

                   
                   const fromClient = !!location.state?.clientName && !location.state?.fromJobDetails;

                    navigate(`/job-details/${jobId}`, {
                        state: fromClient
                            ? {
                                jobId,
                                source: 'client-dashboard',      
                                clientId: location.state.clientId,
                                clientName: location.state.clientName,
                                isAllClients: false,
                                fromIntegration: true,          
                                activeTab: 'list-summary'
                            }
                            : {
                                jobId,
                                source: 'list-upload',           
                                isAllClients: false,
                                activeTab: 'list-summary'
                            }
                    });
                };

                return (
                    <span
                        onClick={handleClick}
                        style={{
                            color: '#2563eb',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontWeight: 500
                        }}
                    >
                        {jobId}
                    </span>
                );
            }
        },

        { headerName: 'Type', field: 'type', minWidth: 250, filter: 'agTextColumnFilter', },
        {
            headerName: 'Status',
            field: 'status',
            minWidth: 180,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                const config = statusConfig[params.value];
                if (!config) return params.value;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon
                            name={config.icon}
                            size={16}
                            color={config.color}
                        />
                        <span style={{ color: config.color, fontWeight: 500 }}>
                            {config.label}
                        </span>
                    </div>
                );
            }
        },
        {
            headerName: 'Description', field: 'description', flex: 2, minWidth: 200, filter: 'agTextColumnFilter', cellStyle: { whiteSpace: 'normal', wordBreak: 'break-word', fontWeight: 'normal' },
            autoHeight: true, wrapText: true, cellRenderer: DescriptionCellRenderer, tooltipField: 'description'
        },
        { headerName: 'Mail Date', field: 'mailDate', flex: 2, minWidth: 220, filter: 'agTextColumnFilter',   comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB), }
    ], []);

   
    const detailsColumnDefs = useMemo(() => [
        {
            headerName: 'Job ID',
            field: 'jobId',
            minWidth: 180,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => (
                <span style={{ fontWeight: isTotalRow(params) ? 'bold' : 'normal' }}>
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Message',
            field: 'issue',
            minWidth: 180,
            flex: 0.8,
            tooltipField: 'issueTooltip',
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                // TOTAL row → show nothing
                if (isTotalRow(params)) {
                    return null;
                }

                const { processingStatus, errorMessage } = params.data || {};
                const isSuccess = processingStatus === 'SUCCESS';
                if (isSuccess) {
                    return (
                        <span style={{ color: '#15803d', fontWeight: 500 }}>
                            SUCCESS
                        </span>
                    );
                }
                const fullMsg = errorMessage || 'Error';
                const truncated = fullMsg.length > 12
                    ? `${fullMsg.substring(0, 12)}...`
                    : fullMsg;

                return (
                    <span style={{ color: '#dc2626', fontWeight: 500 }}>
                        {truncated}
                    </span>
                );
            }
        },
        {
            headerName: 'Order Number',
            field: 'orderNumber',
            minWidth: 200,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                if (isTotalRow(params)) {
                    return <span style={{ fontWeight: 'bold' }}>TOTAL</span>;
                }
                return <span>{params.value}</span>;
            }
        },
        {
            headerName: 'List Name',
            field: 'listName',
            minWidth: 220,
            flex: 1.5,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                if (isTotalRow(params)) {
                    return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
                }
                return <TooltipText text={params.value} maxLength={30} />;
            }
        },
        {
            headerName: 'List Segment',
            field: 'segment',
            minWidth: 300,
            flex: 2,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                if (isTotalRow(params)) {
                    return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
                }
                return <TooltipText text={params.value} maxLength={25} />;
            }
        },
        {
            headerName: 'Mail Date',
            field: 'mailDate',
            minWidth: 180,
              comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => (
                <span style={{ fontWeight: isTotalRow(params) ? 'bold' : 'normal' }}>
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Date Needed',
            field: 'dateNeeded',
              comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            minWidth: 200,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => (
                <span style={{ fontWeight: isTotalRow(params) ? 'bold' : 'normal' }}>
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Quantity Ordered',
            field: 'quantityOrdered',
            minWidth: 220,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => (
                <span style={{ fontWeight: isTotalRow(params) ? 'bold' : 'normal' }}>
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Order Type',
            field: 'orderType',
            minWidth: 180,
            filter: 'agTextColumnFilter',
            flex: 0.8,
            cellRenderer: (params) => (
                <span style={{ fontWeight: isTotalRow(params) ? 'bold' : 'normal' }}>
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'List Manager',
            field: 'listManager',
            filter: 'agTextColumnFilter',
            minWidth: 200,
            flex: 1,
            cellRenderer: (params) => {
                if (isTotalRow(params)) {
                    return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
                }
                return <TooltipText text={params.value} maxLength={20} />;
            }
        },
        {
            headerName: 'R/E/H',
            field: 'listtypeid',
            minWidth: 200,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => (
                <span style={{ fontWeight: isTotalRow(params) ? 'bold' : 'normal' }}>
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Total Act. Cost',
            field: 'totalActCost',
            filter: 'agTextColumnFilter',
            minWidth: 220,
            flex: 0.8,
            cellRenderer: (params) => (
                <span style={{
                    textAlign: 'right',
                    display: 'block',
                    fontWeight: isTotalRow(params) ? 'bold' : 'normal'
                }}>
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Change Type',
            field: 'changeType',
            filter: 'agTextColumnFilter',
            minWidth: 180,
            flex: 0.8,
            cellRenderer: (params) => {
                if (isTotalRow(params)) {
                    return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
                }
                return <TooltipText text={params.value} maxLength={15} />;
            }
        }
    ], []);

    return (
        <>
            <div>

                {!viewOnly && (
                    <div className='tallies-header'>
                        <span className="tallies-content-title">Upload List</span>
                    </div>

                )}

              
                {!viewOnly && (
                    <div className="tallies-upload-section pt-4">
                        <label className="tallies-upload-label">
                            File Upload <span className="required-field">*</span>
                        </label>

                        {!uploadedFile ? (
                            <div
                                className={`tallies-upload-box 
                    ${isDragging ? 'tallies-dragging' : ''} 
                    ${isFormatInvalid ? 'format-invalid' : ''}
                `}
                                style={{
                                    border: isFormatInvalid ? '1.5px dashed #dc2626' : undefined,
                                    backgroundColor: isFormatInvalid ? '#fff' : undefined,
                                    transition: 'border-color 0.2s ease'
                                }}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => !isValidating && fileInputRef.current?.click()}
                            >
                                <Upload
                                    size={24}
                                    className={`tallies-upload-box-icon ${isDragging ? 'tallies-dragging' : ''}`}
                                />

                                {isValidating ? (
                                    <p className="tallies-upload-box-text" style={{ color: '#6b7280' }}>
                                        ⏳ Validating file format...
                                    </p>
                                ) : (
                                    <p className="tallies-upload-box-text">
                                        Drag and drop Excel (.xlsx or .xls) file here, or{' '}
                                        <span className="tallies-browse-text">Browse...</span>
                                    </p>
                                )}

                                <input
                                    ref={fileInputRef}
                                    id="tallies-file-input"
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileUpload}
                                    className="tallies-hidden-input"
                                />
                            </div>
                        ) : (
                            <div className="tallies-uploaded-file-card">
                                <div className="tallies-file-info-inline">
                                    <Icon name="package-icon" size={24} />
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div className="tallies-file-name">{uploadedFile.name}</div>
                                        <div className="tallies-file-size">
                                            {formatFileSize(uploadedFile.size)}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    className="tallies-file-remove-btn"
                                    onClick={handleRemoveFile}
                                    title="Remove file"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        )}

                        {fileError && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '6px',
                                    color: '#dc2626',
                                    fontSize: '12px',
                                    marginTop: '6px',
                                    padding: '6px 10px',
                                }}
                            >
                                <span>{fileError}</span>
                            </div>
                        )}
                    </div>
                )}


             

              
                {!viewOnly && (
                    <div className="download-upload-row">
                        <div
                            className="tallies-download-template"
                            onClick={downloadTemplate}
                            style={{ cursor: 'pointer' }}
                        >
                            <Download size={14} className="tallies-download-template-icon" />
                            <span className="tallies-download-template-text underlined-text">
                                Download template
                            </span>
                        </div>

                        {uploadedFile && (
                            <button
                                className={`upload-file-btn ${!isUploadEnabled ? 'disabled' : ''}`}
                                onClick={handleUploadFileToAPI}
                                disabled={!isUploadEnabled || isUploading}
                            >
                                {isUploading ? (
                                    <>
                                        <span className="loading-spinner">⟳</span>
                                        <span>Uploading...</span>
                                    </>
                                ) : (
                                    <span>Upload File</span>
                                )}
                            </button>
                        )}
                    </div>
                )}

               
                <div className="tallies-header mb-4">
                    <span className="tallies-content-title">List Upload Search</span>
                </div>

             
                <div className='filter-container'>
                    <div className="filter-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0px 10px 0px 0px',
                        alignItems: 'center',
                        borderRadius: '8px 8px 0px 0px'
                    }}>
                        <div className='' style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: "20px", fontWeight: 600, paddingLeft: "20px", paddingRight: "20px", paddingTop: "10px", paddingBottom: "10px" }}>
                            Filters
                        </div>
                        <button
                            onClick={toggleFilters}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            aria-label={showFilters ? 'Hide filters' : 'Show filters'}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                    transition: 'transform 0.2s ease',
                                    transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                                    color: 'black'
                                }}
                            >
                                <polyline points="18 15 12 9 6 15" />
                            </svg>
                        </button>
                    </div>
                    {showFilters && (
                        <hr
                            style={{
                                margin: 0,
                                padding: 0,
                                height: 0,
                                backgroundColor: 'transparent'
                            }}
                        />
                    )}
                    {showFilters && (
                        <>
                           
                            <div className='d-flex flex-wrap gap-4 mb-3 pt-4 px-4'>
                                <div className='filter-client' style={{ width: '100%' }}>
                                    <label className='filter-label'>Client(s) Selected:</label>
                                    <div className='filter-input-group' ref={clientDropdownRef} style={{ position: 'relative' }}>
                                        <div
                                            style={{
                                                minHeight: "30px",
                                                width: '100%',
                                                border: `1px solid ${clientError ? '#dc3545' : '#ccc'}`,
                                                borderRadius: '4px',
                                                padding: '4px 8px',
                                                cursor: 'text',
                                                backgroundColor: '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                            onClick={() => {
                                                setShowClientDropdown(true);
                                                document.getElementById('client-search-input')?.focus();
                                            }}
                                        >
                                            {selectedClients.length > 1 ? (
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    backgroundColor: '#e9ecef',
                                                    padding: '2px 6px',
                                                    borderRadius: '3px',
                                                    fontSize: '13px',
                                                    gap: '4px'
                                                }}>
                                                    {selectedClients.length} clients selected
                                                    <span
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleClearAllClients();
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            color: '#666',
                                                            marginLeft: '2px'
                                                        }}
                                                    >
                                                        ×
                                                    </span>
                                                </span>
                                            ) : (
                                                selectedClients.map((client) => (
                                                    <span
                                                        key={client.clientID}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            backgroundColor: '#e9ecef',
                                                            padding: '2px 6px',
                                                            borderRadius: '3px',
                                                            fontSize: '13px',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        {client.clientName}
                                                        <span
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const newClients = selectedClients.filter(c => c.clientID !== client.clientID);
                                                                setSelectedClients(newClients);
                                                                setSelectedClientIds(newClients.map(c => c.clientID));
                                                                if (newClients.length === 1) {
                                                                    setSelectedClient(newClients[0].clientName);
                                                                } else if (newClients.length > 1) {
                                                                    setSelectedClient(`${newClients.length} clients selected`);
                                                                } else {
                                                                    setSelectedClient('');
                                                                }
                                                            }}
                                                            style={{
                                                                cursor: 'pointer',
                                                                fontSize: '14px',
                                                                color: '#666',
                                                                marginLeft: '2px'
                                                            }}
                                                        >
                                                            ×
                                                        </span>
                                                    </span>
                                                ))
                                            )}

                                            <input
                                                id="client-search-input"
                                                type="text"
                                                placeholder={selectedClients.length === 0 ? "Select clients..." : ""}
                                                value={clientSearchTerm}
                                                onChange={(e) => {
                                                    setClientSearchTerm(e.target.value);
                                                    setShowClientDropdown(true);
                                                }}
                                                onFocus={() => setShowClientDropdown(true)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Backspace' && clientSearchTerm === '' && selectedClients.length > 0) {
                                                        const newClients = selectedClients.slice(0, -1);
                                                        setSelectedClients(newClients);
                                                        setSelectedClientIds(newClients.map(c => c.clientID));
                                                        if (newClients.length === 1) {
                                                            setSelectedClient(newClients[0].clientName);
                                                        } else if (newClients.length > 1) {
                                                            setSelectedClient(`${newClients.length} clients selected`);
                                                        } else {
                                                            setSelectedClient('');
                                                        }
                                                    }
                                                }}
                                                style={{
                                                    flex: 1,
                                                    minWidth: '120px',
                                                    border: 'none',
                                                    outline: 'none',
                                                    padding: '6px 0',
                                                    fontSize: '14px',
                                                    background: 'transparent'
                                                }}
                                            />
                                        </div>

                                        {clientError && (
                                            <div style={{
                                                color: '#dc3545',
                                                fontSize: '12px',
                                                marginTop: '4px',
                                                paddingLeft: '4px'
                                            }}>
                                                {clientError}
                                            </div>
                                        )}

                                        {showClientDropdown && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                backgroundColor: '#fff',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                zIndex: 1000,
                                                maxHeight: '300px',
                                                overflow: 'hidden',
                                                marginTop: '4px',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #eee',
                                                    backgroundColor: '#f8f9fa'
                                                }}>
                                                    <button
                                                        onClick={handleSelectAllClients}
                                                        style={{
                                                            background: 'none',
                                                            border: '1px solid #2E6F9F',
                                                            color: '#2E6F9F',
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        Select All
                                                    </button>
                                                    <button
                                                        onClick={handleClearAllClients}
                                                        style={{
                                                            background: 'none',
                                                            border: '1px solid #dc3545',
                                                            color: '#dc3545',
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        Clear All
                                                    </button>
                                                </div>

                                                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                    {loading ? (
                                                        <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                                                            Loading clients...
                                                        </div>
                                                    ) : filteredClients.length > 0 ? (
                                                        filteredClients.map((client) => (
                                                            <div
                                                                key={client.clientID}
                                                                onClick={() => handleClientCheckboxChange(client)}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    borderBottom: '1px solid #f0f0f0',
                                                                    backgroundColor: selectedClients.some(c => c.clientID === client.clientID) ? '#e6f7ff' : '#fff',
                                                                    transition: 'background-color 0.2s'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!selectedClients.some(c => c.clientID === client.clientID)) {
                                                                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!selectedClients.some(c => c.clientID === client.clientID)) {
                                                                        e.currentTarget.style.backgroundColor = '#fff';
                                                                    }
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedClients.some(c => c.clientID === client.clientID)}
                                                                    onChange={() => handleClientCheckboxChange(client)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{ marginRight: '8px', cursor: 'pointer' }}
                                                                />
                                                                <span>{client.clientName}</span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                                                            No clients found matching "{clientSearchTerm}"
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                          
                            <div className='d-flex flex-wrap gap-4 mb-3 pt-2 px-4'>
                                <div className='mail-date-filter'>
                                    <label className='mail-date-filter-label'>Mail Date</label>
                                    <div className='mail-date-input-container'>
                                        <div className="date-input-wrapper">
                                            <input
                                                type="date"
                                                className={`mail-date-input ${!mailDateFrom ? 'empty' : ''}`}
                                                value={mailDateFrom}
                                              
                                                onChange={(e) => {
                                                    const newFromDate = e.target.value;
                                                   
                                                    if (mailDateTo && newFromDate > mailDateTo) {
                                                        alert('Start date cannot be after end date');
                                                        return; 
                                                    }
                                                    setMailDateFrom(newFromDate);
                                                }}
                                                max={mailDateTo || undefined} 
                                                onFocus={() => setIsFocused(true)}
                                                onBlur={() => setIsFocused(false)}

                                            />
                                        </div>
                                        <span className='arrow-icon'>→</span>
                                        <div className="date-input-wrapper">
                                            <input
                                                type="date"
                                                className={`mail-date-input ${!mailDateTo ? 'empty' : ''}`}
                                                value={mailDateTo}
                                               
                                                onChange={(e) => {
                                                    const newToDate = e.target.value;
                                                  
                                                    if (mailDateFrom && newToDate < mailDateFrom) {
                                                        alert('End date cannot be before start date');
                                                        return; 
                                                    }
                                                    setMailDateTo(newToDate);
                                                }}
                                                min={mailDateFrom || undefined} 
                                                onFocus={() => setIsFocused(true)}
                                                onBlur={() => setIsFocused(false)}

                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="job-type-container">
                                    <label className='job-type-label'>Job Type</label>
                                    <select
                                        className='job-type-select'
                                        value={selectedJobTypeId}
                                        onChange={(e) => setSelectedJobTypeId(e.target.value)}
                                        disabled={loadingJobTypes}

                                    >
                                        <option value="">Select</option>
                                        {loadingJobTypes ? (
                                            <option value="" disabled>Loading job types...</option>
                                        ) : jobTypes.length > 0 ? (
                                            jobTypes.map(jobType => (
                                                <option
                                                    key={jobType.jobTypeID}
                                                    value={jobType.jobTypeID}
                                                >
                                                    {jobType.jobTypeName}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="" disabled>No job types available</option>
                                        )}
                                    </select>
                                </div>

                                <div className="jobid-container">
                                    <label className="jobid-label">Job ID</label>
                                    <input
                                        type="text"
                                        className="jobid-input"
                                        value={jobCode}
                                        onChange={(e) => setJobCode(e.target.value)}

                                        placeholder="Enter Job ID"
                                    />
                                </div>
                            </div>

                            {/* ─── Search & Clear Buttons ───────────────────── */}
                            <div className="button-container">
                                <div className="button-wrapper">
                                    <button
                                        className="button-search"
                                        onClick={handleSearch}
                                        disabled={searchLoading}
                                    >
                                        {searchLoading ? 'Searching...' : 'Search'}
                                    </button>
                                </div>
                                <div className="button-wrapper">
                                    <button
                                        className="button-clear"
                                        onClick={handleClear}
                                        disabled={searchLoading}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                
                <div style={{ marginTop: '16px' }}>
                    <AgGrid
                        rowData={filteredData}
                        columnDefs={columnDefs}
                        pagination={true}
                        paginationPageSize={10}
                        paginationPageSizeSelector={[10, 20, 50]}
                        height="300px"
                        enableSearch={false}
                        enableSorting={true}
                        enableFilter={true}
                        hideExportButton={true}
                        hideSideBar={true}
                        hideSelectAll={true}
                        getRowId={(params) => params.data.id}
                        noRowsMessage={
                            listLoading ? 'Loading upload history...' : 'No list uploads found'
                        }
                    />
                </div>

               
                {selectedJobForDetails && (
                    <div style={{ marginTop: '32px' }}>
                        <div className="tallies-header">
                            <span className="tallies-content-title">List of Lists Details</span>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            marginTop: '12px',
                            marginBottom: '4px',
                            flexWrap: 'wrap',
                            gap: '12px'
                        }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <strong style={{ fontSize: '14px', color: '#333' }}>Upload Version</strong>
                                <div ref={uploadVersionDropdownRef} style={{ position: 'relative' }}>
                                    <div
                                        onClick={() => {
                                            if (!loadingVersions && uploadVersions.length > 0) {
                                                setShowUploadVersionDropdown(!showUploadVersionDropdown);
                                            }
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            minWidth: '320px',
                                            padding: '8px 12px',
                                            border: hasVersionError
                                                ? '1.5px solid #dc2626'
                                                : '1px solid #ccc',
                                            borderRadius: '4px',
                                            backgroundColor: loadingVersions || uploadVersions.length === 0
                                                ? '#f5f5f5'
                                                : '#fff',
                                            cursor: loadingVersions || uploadVersions.length === 0 ? 'not-allowed' : 'pointer',
                                            gap: '8px',
                                            opacity: loadingVersions || uploadVersions.length === 0 ? 0.7 : 1
                                        }}
                                    >
                                        <span style={{
                                            color: !selectedUploadVersion ? '#999' : '#333'
                                        }}>
                                            {loadingVersions
                                                ? 'Loading versions...'
                                                : uploadVersions.length === 0
                                                    ? 'No versions available'
                                                    : selectedUploadVersion?.name || 'Select version'}
                                        </span>
                                        <ChevronDown size={16} style={{
                                            color: '#666',
                                            transform: showUploadVersionDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease'
                                        }} />
                                    </div>

                                    {showUploadVersionDropdown && uploadVersions.length > 0 && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: '#fff',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            zIndex: 1000,
                                            marginTop: '4px',
                                            maxHeight: '280px',
                                            overflowY: 'auto',
                                            minWidth: '320px'
                                        }}>
                                            {uploadVersions.map((version) => (
                                                <div
                                                    key={version.id}
                                                    onClick={() => handleUploadVersionChange(version)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedUploadVersion?.id === version.id ? '#e6f7ff' : '#fff',
                                                        borderBottom: '1px solid #f0f0f0',
                                                        transition: 'background-color 0.2s',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '8px'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (selectedUploadVersion?.id !== version.id) {
                                                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (selectedUploadVersion?.id !== version.id) {
                                                            e.currentTarget.style.backgroundColor = '#fff';
                                                        }
                                                    }}
                                                >
                                                    <span style={{ flex: 1, fontSize: '13px' }}>
                                                        {version.name}
                                                    </span>

                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        {version.isLatestVersion && (
                                                            <span style={{
                                                                fontSize: '10px',
                                                                fontWeight: 600,
                                                                padding: '2px 6px',
                                                                borderRadius: '10px',
                                                                backgroundColor: '#dbeafe',
                                                                color: '#1e40af'
                                                            }}>
                                                                Latest
                                                            </span>
                                                        )}
                                                        <span style={{
                                                            fontSize: '10px',
                                                            fontWeight: 600,
                                                            padding: '2px 6px',
                                                            borderRadius: '10px',
                                                            backgroundColor: version.versionStatus === 'SUCCESS' ? '#dcfce7' : '#fee2e2',
                                                            color: version.versionStatus === 'SUCCESS' ? '#15803d' : '#dc2626'
                                                        }}>
                                                            {version.versionStatus}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {hasVersionError && (
                            <div style={{
                                marginTop: '4px',
                                marginBottom: '12px',
                                color: '#dc2626',
                                fontSize: '13px'
                            }}>
                                This version contains an error, please reference the table to understand what went wrong.
                            </div>
                        )}

                       
                        <div style={{ marginTop: '16px', position: 'relative' }}>
                            {batchLoading && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        zIndex: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <AisScreenLoader />
                                </div>
                            )}
                            <AgGrid
                                rowData={batchDetailsRows}
                                pinnedBottomRowData={[batchTotalsRow]}
                                columnDefs={detailsColumnDefs}
                                paginationPageSizeSelector={[10, 20, 50]}
                                height="400px"
                                enableSearch={false}
                                enableSorting={true}
                                enableFilter={true}
                                hideExportButton={true}
                                hideSideBar={true}
                                hideSelectAll={true}
                                getRowId={(params) => params.data.id}

                                gridOptions={{
                                    getRowStyle: (params) => {
                                        
                                        if (params.node.rowPinned === 'bottom') {
                                            return {
                                                backgroundColor: '#f0f8ff',
                                                fontWeight: 'bold',
                                                borderTop: '0.5px solid #babfc7'
                                            };
                                        }

                                      
                                        if (
                                            params.data?.processingStatus &&
                                            params.data.processingStatus !== 'SUCCESS'
                                        ) {
                                            return {
                                                backgroundColor: '#FFEAEC'
                                            };
                                        }

                                        return null;
                                    }
                                }}

                            />
                        </div>
                    </div>
                )}
            </div>

            {toastMessage && (
                <SuccessMessage
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setToastMessage('')}
                    autoClose={false}
                />
            )}
        </>
    );
};

export default ListOfList;